import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { APIProvider } from '@vis.gl/react-google-maps';
import AddressAutocomplete from '../components/clients/AddressAutocomplete';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/date-input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { calcolaCodiceFiscale } from '../utils/fiscalCode';
import ClientCompletionBanner from '../components/clients/ClientCompletionBanner';
import {
  Plus, Search, MoreHorizontal, FileText, Mail, Check, X,
  Download, Send, Trash2, Upload, Settings, Loader2, CheckCircle, XCircle, UserPlus, Home, RefreshCw, Copy,
  Sparkles,
} from 'lucide-react';
import { DelegaGeneraleBadge } from '../components/DelegaGenerale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const TIPI_VIA = ['VIA', 'VIALE', 'PIAZZA', 'CORSO', 'LARGO', 'VICOLO', 'STRADA'];

const emptyImmobile = {
  comune: '', provincia: '', tipo_via: 'VIA', via: '', civico: '', interno: '', piano: '',
};

const emptyForm = {
  id: null,
  ospitante_client_id: '',
  ospitato_client_id: '',
  immobile: { ...emptyImmobile },
  data_inizio: '',
  data_fine: '',
  tempo_indeterminato: false,
  doc_tipo: '',
  doc_numero: '',
  doc_data_rilascio: '',
  doc_autorita: '',
  cittadinanza: '',
  notes: '',
};

const emptyNewClient = {
  last_name: '', first_name: '', gender: 'M',
  birth_date: '', birth_abroad: false, birth_comune: '', birth_provincia: '', birth_country: '',
  fiscal_code: '', address: '', address_number: '', city: '', city_provincia: '', postal_code: '',
  phone: '', email: '',
};

// Separa il tipo via (VIA/PIAZZA/...) dal resto del nome — l'OCR restituisce l'indirizzo come
// un unico testo (es. "VIA GIACOMO PUCCINI"), mentre il form Immobile li vuole in due campi.
const splitTipoVia = (addr) => {
  const upper = (addr || '').trim().toUpperCase();
  for (const t of TIPI_VIA) {
    if (upper.startsWith(t + ' ')) {
      return { tipo_via: t, via: upper.slice(t.length + 1).trim() };
    }
  }
  return { tipo_via: 'VIA', via: upper };
};

const NEW_CLIENT_DOC_TIPO_OPTIONS = [
  "Carta d'identità elettronica",
  'Patente',
  'Passaporto',
  'Permesso di soggiorno',
];

// Mappa il tipo documento scelto sul document_type richiesto da /api/ocr/extract-client-data
const OCR_DOC_TYPE_MAP = {
  "Carta d'identità elettronica": 'carta_identita',
  "Carta d'identità cartacea": 'carta_identita',
  'Patente': 'patente',
  'Passaporto': 'passaporto',
  'Permesso di soggiorno': 'permesso_soggiorno',
};

// Etichetta breve con cui il documento caricato viene salvato tra i "Documenti anagrafici"
// del cliente (stessa convenzione già usata in Dimissioni.js).
const DOC_LABEL_MAP = {
  "Carta d'identità elettronica": 'CIE',
  "Carta d'identità cartacea": 'CIE',
  'Patente': 'PAT',
  'Passaporto': 'PASS',
  'Permesso di soggiorno': 'PSE',
};

const Ospitalita = () => {
  const [records, setRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('cerca') || ''); // ?cerca= dal riepilogo cliente
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [newClientDialog, setNewClientDialog] = useState({ open: false, target: null });
  const [newClient, setNewClient] = useState(emptyNewClient);
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClientDocTipo, setNewClientDocTipo] = useState("Carta d'identità elettronica");
  const [extractingNewClientDoc, setExtractingNewClientDoc] = useState(false);
  const [extractingNewClientPse, setExtractingNewClientPse] = useState(false);
  // Documento caricato mentre il cliente non esiste ancora: va allegato in anagrafica solo
  // DOPO la creazione del cliente (vedi saveNewClient).
  const [pendingNewClientDoc, setPendingNewClientDoc] = useState(null); // { file, label, expiryDate }

  // PEC Config
  const [pecDialogOpen, setPecDialogOpen] = useState(false);
  const [pecConfig, setPecConfig] = useState({
    pec_email: '',
    pec_password: '',
    smtp_server: 'mail.postacert.it.net',
    smtp_port: 465,
    imap_server: 'imaps.pec.aruba.it',
    imap_port: 993,
    default_recipient: '',
  });
  const [pecConfigLoaded, setPecConfigLoaded] = useState(null);
  const [pecSaving, setPecSaving] = useState(false);
  const [pecTesting, setPecTesting] = useState(false);
  const [pecTestRecipient, setPecTestRecipient] = useState('');
  const [pecTestResult, setPecTestResult] = useState(null);

  // Send PEC
  const [sendPecDialog, setSendPecDialog] = useState({ open: false, record: null });
  const [sendPecRecipient, setSendPecRecipient] = useState('');
  const [sendingPec, setSendingPec] = useState(false);
  const [sendCopyToOspitante, setSendCopyToOspitante] = useState(false);
  const [sendCopyToOspitato, setSendCopyToOspitato] = useState(false);
  const [sendingRicevuteEmail, setSendingRicevuteEmail] = useState(null);
  const [ricevuteEmailDialog, setRicevuteEmailDialog] = useState({ open: false, record: null, toOspitante: false, toOspitato: false });
  // Anteprima ricevute PEC in modale (invece di download / nuova tab)
  const [previewDialog, setPreviewDialog] = useState({ open: false, url: '', title: '' });

  const uploadInputRefs = useRef({});

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [rRec, rCli] = await Promise.all([
        axios.get(`${API}/ospitalita`),
        axios.get(`${API}/clients`),
      ]);
      setRecords(rRec.data);
      setClients(rCli.data);
    } catch (e) {
      console.error(e);
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const fetchPecConfig = async () => {
    try {
      const res = await axios.get(`${API}/ospitalita-pec-config`);
      setPecConfigLoaded(res.data);
      setPecConfig(c => ({
        ...c,
        pec_email: res.data.pec_email || '',
        smtp_server: res.data.smtp_server || 'mail.postacert.it.net',
        smtp_port: res.data.smtp_port || 465,
        imap_server: res.data.imap_server || 'imaps.pec.aruba.it',
        imap_port: res.data.imap_port || 993,
        default_recipient: res.data.default_recipient || '',
        pec_password: '',
      }));
    } catch (e) {
      /* silent */
    }
  };

  useEffect(() => {
    fetchAll();
    fetchPecConfig();
  }, []);

  const clientLabel = (c) => {
    if (!c) return '';
    if (c.is_company && c.company_name) return c.company_name;
    return `${c.last_name || ''} ${c.first_name || ''}`.trim();
  };

  const clientMap = useMemo(() => {
    const m = {};
    clients.forEach(c => { m[c.id] = c; });
    return m;
  }, [clients]);

  const filteredRecords = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;
    return records.filter(r => (
      (r.ospitante_name || '').toLowerCase().includes(q) ||
      (r.ospitato_name || '').toLowerCase().includes(q) ||
      (r.immobile?.comune || '').toLowerCase().includes(q) ||
      (r.immobile?.via || '').toLowerCase().includes(q)
    ));
  }, [records, search]);

  // Pagination
  const totalItems = filteredRecords.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // ============ CREATE/EDIT DIALOG ==============
  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (rec) => {
    setForm({
      id: rec.id,
      ospitante_client_id: rec.ospitante_client_id,
      ospitato_client_id: rec.ospitato_client_id,
      immobile: { ...emptyImmobile, ...(rec.immobile || {}) },
      data_inizio: rec.data_inizio || '',
      data_fine: rec.data_fine || '',
      tempo_indeterminato: !!rec.tempo_indeterminato,
      doc_tipo: rec.doc_tipo || '',
      doc_numero: rec.doc_numero || '',
      doc_data_rilascio: rec.doc_data_rilascio || '',
      doc_autorita: rec.doc_autorita || '',
      cittadinanza: rec.cittadinanza || '',
      notes: rec.notes || '',
    });
    setDialogOpen(true);
  };

  const openClone = (rec) => {
    // Clona il record: pre-compila il form senza id (verrà creato un nuovo record)
    // e senza dati specifici della singola pratica (PDF firmato / PEC / ricevute)
    setForm({
      id: null,
      ospitante_client_id: rec.ospitante_client_id,
      ospitato_client_id: rec.ospitato_client_id,
      immobile: { ...emptyImmobile, ...(rec.immobile || {}) },
      data_inizio: rec.data_inizio || '',
      data_fine: rec.data_fine || '',
      tempo_indeterminato: !!rec.tempo_indeterminato,
      doc_tipo: rec.doc_tipo || '',
      doc_numero: rec.doc_numero || '',
      doc_data_rilascio: rec.doc_data_rilascio || '',
      doc_autorita: rec.doc_autorita || '',
      cittadinanza: rec.cittadinanza || '',
      notes: rec.notes || '',
    });
    setDialogOpen(true);
    toast.success('Pratica clonata — modifica se serve e salva per creare la copia');
  };

  const autofillImmobileFromOspitante = async () => {
    if (!form.ospitante_client_id) {
      toast.error('Seleziona prima l\'ospitante');
      return;
    }
    try {
      const res = await axios.get(`${API}/assets?client_id=${form.ospitante_client_id}&asset_type=casa`);
      const casa = (res.data || [])[0];
      if (!casa) {
        toast.error('Nessuna casa collegata all\'ospitante');
        return;
      }
      setForm(f => ({
        ...f,
        immobile: {
          ...f.immobile,
          comune: casa.city || '',
          provincia: casa.provincia || '',
          via: casa.indirizzo || '',
          civico: casa.numero_civico || '',
        }
      }));
      toast.success('Immobile precompilato dai dati della casa');
    } catch (e) {
      toast.error('Impossibile recuperare la casa');
    }
  };

  const handleSave = async () => {
    if (!form.ospitante_client_id || !form.ospitato_client_id) {
      toast.error('Seleziona Ospitante e Ospitato');
      return;
    }
    if (form.ospitante_client_id === form.ospitato_client_id) {
      toast.error('Ospitante e Ospitato devono essere diversi');
      return;
    }
    if (!form.data_inizio) {
      toast.error('Data inizio obbligatoria');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ospitante_client_id: form.ospitante_client_id,
        ospitato_client_id: form.ospitato_client_id,
        immobile: form.immobile,
        data_inizio: form.data_inizio,
        data_fine: form.tempo_indeterminato ? '' : (form.data_fine || ''),
        tempo_indeterminato: !!form.tempo_indeterminato,
        doc_tipo: form.doc_tipo || '',
        doc_numero: form.doc_numero || '',
        doc_data_rilascio: form.doc_data_rilascio || '',
        doc_autorita: form.doc_autorita || '',
        cittadinanza: form.cittadinanza || '',
        notes: form.notes,
      };
      if (form.id) {
        await axios.put(`${API}/ospitalita/${form.id}`, payload);
        toast.success('Ospitalità aggiornata');
      } else {
        await axios.post(`${API}/ospitalita`, payload);
        toast.success('Ospitalità creata');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rec) => {
    if (!window.confirm(`Eliminare la dichiarazione di ospitalità di ${rec.ospitante_name} → ${rec.ospitato_name}?`)) return;
    try {
      await axios.delete(`${API}/ospitalita/${rec.id}`);
      toast.success('Record eliminato');
      fetchAll();
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  const downloadPdfPreFirma = async (rec) => {
    try {
      const res = await axios.get(`${API}/ospitalita/${rec.id}/generate-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ospitalita_${rec.ospitante_name || 'ospitante'}_${rec.ospitato_name || 'ospitato'}.pdf`.replace(/\s+/g, '_');
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore generazione PDF');
    }
  };

  const downloadSignedPdf = async (rec) => {
    try {
      const res = await axios.get(`${API}/ospitalita/${rec.id}/signed-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = rec.signed_pdf_original_name || 'firmato.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore download');
    }
  };

  const handleUploadSigned = async (rec, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Il file deve essere in formato PDF');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      toast.loading('Caricamento PDF...', { id: `upload-${rec.id}` });
      await axios.post(`${API}/ospitalita/${rec.id}/upload-signed-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('PDF firmato caricato', { id: `upload-${rec.id}` });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload', { id: `upload-${rec.id}` });
    }
  };

  // ============ RICEVUTE PEC (IMAP + STAMPA) ==============
  const [fetchingRicevute, setFetchingRicevute] = useState(null);

  const fetchRicevute = async (rec) => {
    if (!rec.pec_sent) {
      toast.error('Devi prima inviare la PEC');
      return;
    }
    setFetchingRicevute(rec.id);
    try {
      toast.loading('Scarico ricevute PEC dalla casella IMAP...', { id: `fetch-${rec.id}` });
      const res = await axios.post(`${API}/ospitalita/${rec.id}/fetch-ricevute`);
      const found = res.data.found || [];
      if (found.length === 0) {
        toast.info('Nessuna nuova ricevuta trovata (potrebbe non essere ancora arrivata)', { id: `fetch-${rec.id}` });
      } else {
        toast.success(`Trovate: ${found.join(' + ')}`, { id: `fetch-${rec.id}` });
      }
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore IMAP', { id: `fetch-${rec.id}` });
    } finally {
      setFetchingRicevute(null);
    }
  };

  const fetchAllRicevute = async () => {
    try {
      toast.loading('Controllo ricevute PEC per tutte le ospitalità...', { id: 'fetch-all' });
      const res = await axios.post(`${API}/ospitalita/fetch-ricevute-all`);
      toast.success(`Controllate ${res.data.processed} ospitalità, aggiornate ${res.data.updated}`, { id: 'fetch-all' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore IMAP', { id: 'fetch-all' });
    }
  };

  const downloadRicevuta = async (rec, tipo, mode = 'clean') => {
    try {
      // 'clean' ora restituisce il PDF UNIFICATO (CRM + originale + daticert.xml appendice)
      const url = `${API}/ospitalita/${rec.id}/ricevuta/${tipo}/${mode}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const tipoLabel = tipo === 'accettazione' ? 'Accettazione' : 'Consegna';
      const title = `Ricevuta ${tipoLabel} — ${rec.ospitante_name || rec.id.slice(0, 8)}`;
      setPreviewDialog({ open: true, url: blobUrl, title });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ricevuta non ancora disponibile');
    }
  };

  // Scarica PDF UNIFICATO Accettazione+Consegna in un unico documento multi-pagina
  const downloadRicevuteCombinate = async (rec) => {
    try {
      const url = `${API}/ospitalita/${rec.id}/ricevuta/combined/clean`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const title = `Ricevute PEC — ${rec.ospitante_name || rec.id.slice(0, 8)}`;
      setPreviewDialog({ open: true, url: blobUrl, title });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ricevute non ancora disponibili');
    }
  };

  const closePreview = () => {
    if (previewDialog.url) {
      try { window.URL.revokeObjectURL(previewDialog.url); } catch (_e) {}
    }
    setPreviewDialog({ open: false, url: '', title: '' });
  };

  const openSendPec = (rec) => {
    if (!rec.signed_pdf_filename) {
      toast.error('Devi prima caricare il PDF unico firmato');
      return;
    }
    setSendPecDialog({ open: true, record: rec });
    setSendPecRecipient(pecConfigLoaded?.default_recipient || '');
    setSendCopyToOspitante(false);
    setSendCopyToOspitato(false);
  };

  const confirmSendPec = async () => {
    const rec = sendPecDialog.record;
    if (!rec) return;
    if (!sendPecRecipient || !sendPecRecipient.includes('@')) {
      toast.error('Inserisci un destinatario valido');
      return;
    }
    setSendingPec(true);
    try {
      const sendCopyTo = [
        ...(sendCopyToOspitante ? ['ospitante'] : []),
        ...(sendCopyToOspitato ? ['ospitato'] : []),
      ];
      const res = await axios.post(`${API}/ospitalita/${rec.id}/send-pec`, {
        recipient_email: sendPecRecipient,
        send_copy_to: sendCopyTo,
      });
      toast.success(res.data.message || 'PEC inviata');
      if (sendCopyTo.length > 0) {
        const sentTo = res.data.copy_sent_to || [];
        const errors = res.data.copy_errors || [];
        if (sentTo.length > 0) toast.success(`Copia email inviata a: ${sentTo.join(', ')}`);
        errors.forEach(err => toast.error(`Copia email non inviata — ${err}`));
      }
      setSendPecDialog({ open: false, record: null });
      fetchAll();
      // Auto-aggiornamento silenzioso: 6s dopo l'invio controlla le ricevute PEC
      // (solo le ultime 10 ospitalità ancora scoperte).
      setTimeout(() => {
        axios.post(`${API}/ospitalita/fetch-ricevute-all?limit=10`)
          .then(() => fetchAll())
          .catch(() => {});
      }, 6000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio PEC');
    } finally {
      setSendingPec(false);
    }
  };

  const confirmSendRicevuteEmail = async () => {
    const rec = ricevuteEmailDialog.record;
    if (!rec) return;
    const sendTo = [
      ...(ricevuteEmailDialog.toOspitante ? ['ospitante'] : []),
      ...(ricevuteEmailDialog.toOspitato ? ['ospitato'] : []),
    ];
    if (sendTo.length === 0) {
      toast.error('Seleziona almeno un destinatario');
      return;
    }
    setSendingRicevuteEmail(rec.id);
    try {
      const res = await axios.post(`${API}/ospitalita/${rec.id}/send-ricevute-email`, { send_to: sendTo });
      const sentTo = res.data.sent_to || [];
      const errors = res.data.errors || [];
      if (sentTo.length > 0) toast.success(`Ricevute inviate a: ${sentTo.join(', ')}`);
      errors.forEach(err => toast.error(err));
      setRicevuteEmailDialog({ open: false, record: null, toOspitante: false, toOspitato: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nell\'invio delle ricevute al cliente');
    } finally {
      setSendingRicevuteEmail(null);
    }
  };

  // ============ NEW CLIENT INLINE =============
  const openNewClient = (target) => {
    setNewClient(emptyNewClient);
    setNewClientDocTipo("Carta d'identità elettronica");
    setPendingNewClientDoc(null);
    setNewClientDialog({ open: true, target });
  };

  // L'immobile dell'ospitalità di solito coincide con la residenza dell'ospitante: propaga
  // l'indirizzo anche quando viene scritto A MANO nel form "Nuovo cliente" (l'estrazione OCR
  // lo fa già da sola in handleExtractNewClientDoc, qui serve lo stesso per l'inserimento manuale).
  const syncImmobileFromAddress = (updatedClient) => {
    if (newClientDialog.target !== 'ospitante') return;
    const { tipo_via, via } = splitTipoVia(updatedClient.address);
    setForm(f => ({
      ...f,
      immobile: {
        ...f.immobile,
        comune: updatedClient.city || f.immobile.comune,
        provincia: updatedClient.city_provincia || f.immobile.provincia,
        tipo_via: updatedClient.address ? tipo_via : f.immobile.tipo_via,
        via: updatedClient.address ? via : f.immobile.via,
        civico: updatedClient.address_number || f.immobile.civico,
      },
    }));
  };

  // Pulsante "Calcola CF" del form nuovo cliente (stesso di Anagrafica Clienti): calcola il CF
  // dai dati anagrafici e avvisa se appartiene già a un cliente registrato.
  const handleCalcolaCfDaDati = async () => {
    try {
      const cf = await calcolaCodiceFiscale(newClient);
      setNewClient(prev => ({ ...prev, fiscal_code: cf }));
      const existing = matchClientByCf(cf);
      if (existing) {
        toast.warning(`Attenzione: CF già registrato in anagrafica (${existing.last_name} ${existing.first_name})`);
      } else {
        toast.success('Codice Fiscale calcolato');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Prova ad abbinare un cliente esistente in anagrafica tramite codice fiscale estratto
  const matchClientByCf = (cf) => {
    if (!cf) return null;
    return clients.find(c => (c.fiscal_code || '').toUpperCase() === cf.toUpperCase()) || null;
  };

  // Allega il file caricato ai "Documenti anagrafici" del cliente, rinominato come
  // "SIGLA COGNOME NOME" (es. "CIE ROSSI MARIO") — stessa convenzione di Dimissioni.js.
  // Se esiste già un documento dello stesso tipo (stessa sigla) in anagrafica, lo sostituisce
  // invece di aggiungerne un altro — la registrazione di un tipo documento va fatta una sola volta.
  const uploadClientDocument = async (clientId, file, shortLabel, lastName, firstName, expiryDate) => {
    try {
      const label = `${shortLabel} ${lastName || ''} ${firstName || ''}`.replace(/\s+/g, ' ').trim().toUpperCase();
      try {
        const clientRes = await axios.get(`${API}/clients/${clientId}`);
        const existing = (clientRes.data.documents || []).find(
          (d) => d.category === 'anagrafico' && (d.description || '').toUpperCase().startsWith(`${shortLabel} `)
        );
        if (existing) {
          await axios.delete(`${API}/clients/${clientId}/documents/${existing.id}`);
        }
      } catch (_) {
        // non bloccare l'upload se il controllo duplicati fallisce
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', label);
      fd.append('category', 'anagrafico');
      if (expiryDate) fd.append('expiry_date', expiryDate);
      await axios.post(`${API}/clients/${clientId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch (error) {
      toast.error(`Dati estratti, ma il documento non è stato allegato in anagrafica: ${error.response?.data?.detail || 'errore'}`);
    }
  };

  // Estrazione automatica dati dal documento d'identità caricato per l'ospitante/ospitato che
  // si sta registrando: abbina un cliente esistente per CF, altrimenti precompila il form
  // "Nuovo cliente" (il documento si allega dopo la creazione, vedi saveNewClient). Per
  // l'ospitato, il PRIMO documento caricato (box principale, qualunque tipo) popola anche il
  // blocco "Documento dell'Ospitato" della pratica — il secondo upload fisso (sempre PSE) NON
  // lo sovrascrive più: quello resta legato al documento scelto esplicitamente nel Select.
  const handleExtractNewClientDoc = async (file, docTipo = newClientDocTipo, setLoading = setExtractingNewClientDoc, updateDocumentoOspitato = true) => {
    if (!file) return;
    const ocrType = OCR_DOC_TYPE_MAP[docTipo];
    const docLabel = DOC_LABEL_MAP[docTipo] || ocrType.toUpperCase();
    const target = newClientDialog.target;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', ocrType);
      const res = await axios.post(`${API}/ocr/extract-client-data`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.extracted_data || {};
      const c = res.data.client_data || {};
      if (!c.fiscal_code && !c.first_name) {
        toast.error('Nessun dato riconosciuto nel documento — verifica di aver scelto il tipo documento corretto');
        return;
      }

      if (target === 'ospitato' && updateDocumentoOspitato) {
        // Il documento caricato dal box principale (qualunque tipo: CIE, Patente, Passaporto
        // o PSE) aggiorna il blocco "Documento dell'Ospitato" della pratica — il secondo
        // upload fisso (sempre PSE, updateDocumentoOspitato=false) non arriva qui.
        const numero = d.document_number || d.license_number || '';
        setForm(f => ({
          ...f,
          doc_tipo: docTipo.toUpperCase(),
          doc_numero: numero || f.doc_numero,
          doc_data_rilascio: d.issue_date || f.doc_data_rilascio,
          doc_autorita: d.issuing_authority || f.doc_autorita,
          // Cittadinanza specifica (es. "CINESE") invece del generico "EXTRA UE" quando il
          // paese è riconosciuto — "EXTRA UE" resta come fallback se non lo è.
          cittadinanza: (d.nationality_specific || d.nationality || f.cittadinanza || '').toUpperCase(),
        }));
      }

      const match = matchClientByCf(c.fiscal_code);
      if (match) {
        setForm(f => ({ ...f, [target === 'ospitante' ? 'ospitante_client_id' : 'ospitato_client_id']: match.id }));
        await uploadClientDocument(match.id, file, docLabel, match.last_name, match.first_name, d.expiry_date);
        toast.success(`Cliente esistente abbinato: ${match.first_name} ${match.last_name} — documento allegato in anagrafica`);
        setNewClientDialog({ open: false, target: null });
      } else {
        // Unisce con quanto già in form: un secondo documento caricato dopo il primo (es.
        // prima la CIE poi il Passaporto) completa i campi mancanti senza perdere quelli già
        // trovati — ogni campo prende il nuovo valore se il documento lo contiene, altrimenti
        // resta quello già presente.
        // Tutti i campi testo in maiuscolo, sia quelli letti dal documento (l'OCR a volte
        // restituisce Nome Cognome con solo l'iniziale maiuscola) sia quelli già scritti a
        // mano nel form — uniforme con la convenzione del CRM.
        const up = (v) => (v || '').toUpperCase();
        let merged = {
          ...newClient,
          first_name: up(c.first_name) || newClient.first_name,
          last_name: up(c.last_name) || newClient.last_name,
          fiscal_code: c.fiscal_code || newClient.fiscal_code,
          gender: c.gender || newClient.gender,
          birth_date: c.birth_date || newClient.birth_date,
          birth_abroad: c.birth_abroad !== undefined ? c.birth_abroad : newClient.birth_abroad,
          birth_comune: up(c.birth_comune) || newClient.birth_comune,
          birth_provincia: c.birth_provincia || newClient.birth_provincia,
          birth_country: up(c.birth_country) || newClient.birth_country,
          address: up(c.address) || newClient.address,
          address_number: c.address_number || newClient.address_number,
          city: up(c.city) || newClient.city,
          city_provincia: c.city_provincia || newClient.city_provincia,
        };

        // CAP non riportato sul documento (le CIE italiane non lo stampano nell'indirizzo) —
        // se comune e provincia di residenza sono noti e individuano un solo CAP possibile,
        // lo compiliamo in automatico invece di lasciarlo vuoto.
        if (!merged.postal_code && merged.city && merged.city_provincia) {
          try {
            const capRes = await axios.get(`${API}/cap`, { params: { city: merged.city, provincia: merged.city_provincia } });
            if (capRes.data.single && capRes.data.auto_cap) {
              merged = { ...merged, postal_code: capRes.data.auto_cap };
            }
          } catch (e) {
            // il CAP è un aiuto in più: se il lookup fallisce non deve bloccare il resto del flusso
          }
        }

        // L'immobile dell'ospitalità di solito coincide con la residenza dell'ospitante:
        // se stiamo registrando l'ospitante e l'indirizzo è noto, precompila anche il blocco
        // "Immobile dell'ospitalità" (l'operatore può comunque correggerlo se diverso).
        if (target === 'ospitante' && (merged.city || merged.address)) {
          const { tipo_via, via } = splitTipoVia(merged.address);
          setForm(f => ({
            ...f,
            immobile: {
              ...f.immobile,
              comune: merged.city || f.immobile.comune,
              provincia: merged.city_provincia || f.immobile.provincia,
              tipo_via: merged.address ? tipo_via : f.immobile.tipo_via,
              via: merged.address ? via : f.immobile.via,
              civico: merged.address_number || f.immobile.civico,
            },
          }));
        }

        // CF non presente sul documento (es. spesso su Patente/Passaporto) ma ora abbiamo
        // tutti i dati per calcolarlo (cognome, nome, sesso, nascita, luogo) — lo calcoliamo
        // in automatico invece di lasciarlo vuoto.
        const canCalcCf = !merged.fiscal_code && merged.last_name && merged.first_name && merged.gender && merged.birth_date &&
          ((!merged.birth_abroad && merged.birth_comune && merged.birth_provincia) || (merged.birth_abroad && merged.birth_country));
        if (canCalcCf) {
          try {
            const cfRes = await axios.post(`${API}/calculate-fiscal-code`, {
              surname: merged.last_name,
              name: merged.first_name,
              birth_date: merged.birth_date,
              gender: merged.gender,
              birth_abroad: merged.birth_abroad,
              birth_comune: merged.birth_comune,
              birth_provincia: merged.birth_provincia,
              birth_country: merged.birth_country,
            });
            if (cfRes.data.fiscal_code) merged = { ...merged, fiscal_code: cfRes.data.fiscal_code };
          } catch (e) {
            // il calcolo CF è un aiuto in più: se fallisce non deve bloccare il resto del flusso
          }
        }

        // Il CF appena calcolato potrebbe corrispondere a un cliente già esistente (il
        // documento non lo riportava, ma è comunque una persona già in anagrafica) — ricontrolla.
        const matchAfterCalc = canCalcCf ? matchClientByCf(merged.fiscal_code) : null;
        if (matchAfterCalc) {
          setForm(f => ({ ...f, [target === 'ospitante' ? 'ospitante_client_id' : 'ospitato_client_id']: matchAfterCalc.id }));
          await uploadClientDocument(matchAfterCalc.id, file, docLabel, matchAfterCalc.last_name, matchAfterCalc.first_name, d.expiry_date);
          toast.success(`Cliente esistente abbinato: ${matchAfterCalc.first_name} ${matchAfterCalc.last_name} — documento allegato in anagrafica`);
          setNewClientDialog({ open: false, target: null });
        } else {
          setPendingNewClientDoc({ file, label: docLabel, expiryDate: d.expiry_date || '' });
          setNewClient(merged);
          toast.success('Dati estratti — rivedi e completa la creazione (il documento si allega automaticamente)');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'estrazione dati dal documento');
    } finally {
      setLoading(false);
    }
  };

  const saveNewClient = async () => {
    if (!newClient.last_name || !newClient.first_name) {
      toast.error('Cognome e Nome obbligatori');
      return;
    }
    setCreatingClient(true);
    try {
      const res = await axios.post(`${API}/clients`, newClient);
      const created = res.data;
      if (pendingNewClientDoc) {
        await uploadClientDocument(created.id, pendingNewClientDoc.file, pendingNewClientDoc.label, newClient.last_name, newClient.first_name, pendingNewClientDoc.expiryDate);
        setPendingNewClientDoc(null);
      }
      // Per l'ospitante, se l'indirizzo di residenza è noto, registra anche un Bene "Casa" in
      // anagrafica — non solo il blocco Immobile di QUESTA pratica — così il pulsante
      // "Precompila da casa" funziona da subito anche per pratiche future con lo stesso ospitante.
      if (newClientDialog.target === 'ospitante' && newClient.city && newClient.address) {
        try {
          const { via } = splitTipoVia(newClient.address);
          await axios.post(`${API}/assets`, {
            client_id: created.id,
            asset_type: 'casa',
            indirizzo: via,
            numero_civico: newClient.address_number || '',
            city: newClient.city,
            provincia: newClient.city_provincia || '',
            cap: newClient.postal_code || '',
          });
        } catch (e) {
          // Il cliente è comunque creato correttamente: la casa è un aiuto in più, non deve
          // bloccare il flusso se il salvataggio fallisce.
        }
      }
      // Refresh clients & select the new one
      const rCli = await axios.get(`${API}/clients`);
      setClients(rCli.data);
      setForm(f => ({
        ...f,
        [newClientDialog.target === 'ospitante' ? 'ospitante_client_id' : 'ospitato_client_id']: created.id,
      }));
      toast.success('Cliente creato');
      setNewClientDialog({ open: false, target: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione cliente');
    } finally {
      setCreatingClient(false);
    }
  };

  // ============ PEC CONFIG ==============
  const savePecConfig = async () => {
    if (!pecConfig.pec_email.includes('@')) { toast.error('Email PEC non valida'); return; }
    if (!pecConfig.smtp_server) { toast.error('Server SMTP obbligatorio'); return; }
    setPecSaving(true);
    try {
      const payload = { ...pecConfig };
      if (!payload.pec_password) delete payload.pec_password;
      await axios.put(`${API}/ospitalita-pec-config`, payload);
      toast.success('Configurazione salvata');
      fetchPecConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setPecSaving(false);
    }
  };

  const testPec = async () => {
    if (!pecTestRecipient.includes('@')) { toast.error('Destinatario test non valido'); return; }
    setPecTesting(true);
    setPecTestResult(null);
    try {
      const payload = { test_recipient: pecTestRecipient };
      if (pecConfig.pec_email) payload.pec_email = pecConfig.pec_email;
      if (pecConfig.pec_password) payload.pec_password = pecConfig.pec_password;
      if (pecConfig.smtp_server) payload.smtp_server = pecConfig.smtp_server;
      if (pecConfig.smtp_port) payload.smtp_port = pecConfig.smtp_port;
      const res = await axios.post(`${API}/ospitalita-pec-config/test`, payload);
      setPecTestResult({ ok: true, msg: res.data.message });
    } catch (e) {
      setPecTestResult({ ok: false, msg: e.response?.data?.detail || 'Errore test' });
    } finally {
      setPecTesting(false);
    }
  };

  // ============ RENDER =============
  return (
    <div className="space-y-6 animate-fade-in" data-testid="ospitalita-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ospitalità</h1>
          <p className="text-slate-500 text-sm">Dichiarazione di ospitalità (art. 7 D.Lgs. 286/1998) – gestione, PDF e invio via PEC</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchAllRicevute} data-testid="fetch-all-ricevute-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Scarica ricevute PEC
          </Button>
          <Button variant="outline" onClick={() => setPecDialogOpen(true)} data-testid="config-pec-ospitalita-btn">
            <Settings className="w-4 h-4 mr-2" /> Configura PEC
          </Button>
          <Button onClick={openCreate} data-testid="new-ospitalita-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuova Ospitalità
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="search-ospitalita"
                placeholder="Cerca ospitante, ospitato o comune..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Badge variant="outline">{totalItems} record</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-10 text-slate-500">Caricamento...</div>
          ) : totalItems === 0 ? (
            <div className="text-center py-12">
              <UserPlus className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Nessuna dichiarazione di ospitalità presente</p>
              <Button className="mt-4" onClick={openCreate} data-testid="new-ospitalita-empty-btn">
                <Plus className="w-4 h-4 mr-2" /> Crea la prima
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ospitante</TableHead>
                  <TableHead>Ospitato</TableHead>
                  <TableHead>Immobile</TableHead>
                  <TableHead>Decorrenza</TableHead>
                  <TableHead>Data Inserimento</TableHead>
                  <TableHead>PDF Firmato</TableHead>
                  <TableHead>PEC</TableHead>
                  <TableHead>Ricevute al cliente</TableHead>
                  <TableHead className="w-[80px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedRecords.map(rec => {
                  const imm = rec.immobile || {};
                  const addr = `${imm.tipo_via || ''} ${imm.via || ''}${imm.civico ? ', ' + imm.civico : ''}`.trim();
                  const decorrenza = rec.tempo_indeterminato
                    ? `Dal ${formatDate(rec.data_inizio)} · Indeterminato`
                    : rec.data_fine
                      ? `${formatDate(rec.data_inizio)} → ${formatDate(rec.data_fine)}`
                      : `Dal ${formatDate(rec.data_inizio)}`;
                  return (
                    <TableRow key={rec.id} data-testid={`ospitalita-row-${rec.id}`}>
                      <TableCell className="font-medium">
                        {rec.ospitante_name || '—'}
                        <div className="mt-1"><DelegaGeneraleBadge clientId={rec.ospitante_client_id} clientName={rec.ospitante_name} /></div>
                      </TableCell>
                      <TableCell>
                        {rec.ospitato_name || '—'}
                        <div className="mt-1"><DelegaGeneraleBadge clientId={rec.ospitato_client_id} clientName={rec.ospitato_name} /></div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>{addr || '—'}</div>
                        <div className="text-xs text-slate-500">
                          {imm.comune}{imm.provincia ? ` (${imm.provincia})` : ''}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{decorrenza}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {rec.created_at ? formatDate(rec.created_at.slice(0, 10)) : '—'}
                      </TableCell>
                      <TableCell>
                        {rec.signed_pdf_filename ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            <Check className="w-3 h-3 mr-1" /> Caricato
                          </Badge>
                        ) : (
                          <Badge variant="outline">Non caricato</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {rec.pec_sent ? (
                          <div className="flex flex-col gap-1">
                            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 w-fit">
                              <Mail className="w-3 h-3 mr-1" /> Inviata
                            </Badge>
                            <div className="flex gap-1 flex-wrap items-center">
                              {rec.ricevuta_accettazione ? (
                                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px] px-1.5 py-0" title={`Accettata: ${rec.ricevuta_accettazione.date || ''}${rec.ricevuta_accettazione.identificativo ? '\nID: ' + rec.ricevuta_accettazione.identificativo : ''}`}>
                                  <Check className="w-2.5 h-2.5 mr-0.5" /> Accett.
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400">Accett. attesa</Badge>
                              )}
                              {(rec.ricevuta_mancata_consegna || rec.ricevuta_non_accettazione) && !rec.ricevuta_consegna ? (
                                <>
                                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-[10px] px-1.5 py-0" title={rec.ricevuta_mancata_consegna ? `Mancata consegna: ${rec.ricevuta_mancata_consegna.date || ''}${rec.ricevuta_mancata_consegna.identificativo ? '\nID: ' + rec.ricevuta_mancata_consegna.identificativo : ''}` : `Non accettata: ${rec.ricevuta_non_accettazione.date || ''}`}>
                                    <X className="w-2.5 h-2.5 mr-0.5" /> {rec.ricevuta_mancata_consegna ? 'Mancata cons.' : 'Non accettata'}
                                  </Badge>
                                  {/* la PEC non è arrivata: si rifà la stessa pratica (nuovo record) e si rimanda */}
                                  <button
                                    onClick={() => openClone(rec)}
                                    className="ml-1 px-1.5 py-0 text-[10px] rounded border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center"
                                    title="Non consegnata: crea una copia della stessa pratica da rimandare"
                                  ><Copy className="w-2.5 h-2.5 mr-0.5" /> Clona e rimanda</button>
                                </>
                              ) : rec.ricevuta_consegna ? (
                                <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100 text-[10px] px-1.5 py-0" title={`Consegnata: ${rec.ricevuta_consegna.date || ''}${rec.ricevuta_consegna.identificativo ? '\nID: ' + rec.ricevuta_consegna.identificativo : ''}`}>
                                  <Check className="w-2.5 h-2.5 mr-0.5" /> Cons.
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400">Cons. attesa</Badge>
                              )}
                              {(rec.ricevuta_accettazione || rec.ricevuta_consegna || rec.ricevuta_mancata_consegna) && (
                                <button
                                  onClick={() => downloadRicevuteCombinate(rec)}
                                  className="ml-1 p-0.5 hover:bg-indigo-50 rounded text-indigo-700 border border-indigo-200"
                                  title="Anteprima ricevute PEC (PDF unico)"
                                  data-testid={`preview-ricevute-${rec.id}`}
                                ><FileText className="w-3 h-3" /></button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <Badge variant="outline">Da inviare</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {(rec.ricevuta_accettazione || rec.ricevuta_consegna) ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setRicevuteEmailDialog({ open: true, record: rec, toOspitante: false, toOspitato: false })}
                            disabled={sendingRicevuteEmail === rec.id}
                            className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                            data-testid={`send-ricevute-email-${rec.id}`}
                          >
                            {sendingRicevuteEmail === rec.id
                              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                              : <Mail className="w-4 h-4 mr-1.5" />}
                            Invia al cliente
                          </Button>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          ref={(el) => { uploadInputRefs.current[rec.id] = el; }}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            handleUploadSigned(rec, f);
                            e.target.value = '';
                          }}
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`ospitalita-actions-${rec.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem onClick={() => downloadPdfPreFirma(rec)}>
                              <Download className="w-4 h-4 mr-2" /> Scarica PDF (pre-firma)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => uploadInputRefs.current[rec.id]?.click()}>
                              <Upload className="w-4 h-4 mr-2" /> Carica PDF firmato
                            </DropdownMenuItem>
                            {rec.signed_pdf_filename && (
                              <DropdownMenuItem onClick={() => downloadSignedPdf(rec)}>
                                <FileText className="w-4 h-4 mr-2" /> Vedi PDF firmato
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => openSendPec(rec)}>
                              <Send className="w-4 h-4 mr-2" /> Invia PEC
                            </DropdownMenuItem>
                            {rec.pec_sent && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => fetchRicevute(rec)}
                                  disabled={fetchingRicevute === rec.id}
                                  data-testid={`fetch-ricevute-${rec.id}`}
                                >
                                  {fetchingRicevute === rec.id
                                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    : <RefreshCw className="w-4 h-4 mr-2" />}
                                  Scarica ricevute PEC
                                </DropdownMenuItem>
                                {(rec.ricevuta_accettazione || rec.ricevuta_consegna || rec.ricevuta_mancata_consegna) && (
                                  <DropdownMenuItem onClick={() => downloadRicevuteCombinate(rec)} data-testid={`preview-ricevute-menu-${rec.id}`}>
                                    <FileText className="w-4 h-4 mr-2 text-indigo-600" /> Anteprima ricevute PEC (PDF unico)
                                  </DropdownMenuItem>
                                )}
                                {rec.ricevuta_mancata_consegna && (
                                  <DropdownMenuItem onClick={() => downloadRicevuta(rec, 'mancata_consegna')} className="text-red-600" data-testid={`preview-mancata-${rec.id}`}>
                                    <FileText className="w-4 h-4 mr-2" /> Avviso Mancata Consegna
                                  </DropdownMenuItem>
                                )}
                                {rec.ricevuta_accettazione && rec.ricevuta_accettazione.identificativo && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      navigator.clipboard.writeText(rec.ricevuta_accettazione.identificativo);
                                      toast.success('Identificativo Accettazione copiato');
                                    }}
                                    data-testid={`copy-id-accett-${rec.id}`}
                                  >
                                    <Copy className="w-4 h-4 mr-2 text-emerald-600" />
                                    <span className="truncate max-w-[240px]" title={rec.ricevuta_accettazione.identificativo}>
                                      ID Accett.: {rec.ricevuta_accettazione.identificativo.slice(0, 26)}…
                                    </span>
                                  </DropdownMenuItem>
                                )}
                                {rec.ricevuta_consegna && rec.ricevuta_consegna.identificativo && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      navigator.clipboard.writeText(rec.ricevuta_consegna.identificativo);
                                      toast.success('Identificativo Consegna copiato');
                                    }}
                                    data-testid={`copy-id-cons-${rec.id}`}
                                  >
                                    <Copy className="w-4 h-4 mr-2 text-violet-600" />
                                    <span className="truncate max-w-[240px]" title={rec.ricevuta_consegna.identificativo}>
                                      ID Cons.: {rec.ricevuta_consegna.identificativo.slice(0, 26)}…
                                    </span>
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openEdit(rec)}>
                              <FileText className="w-4 h-4 mr-2" /> Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openClone(rec)} data-testid={`clone-osp-${rec.id}`}>
                              <Copy className="w-4 h-4 mr-2" /> Clona
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(rec)}>
                              <Trash2 className="w-4 h-4 mr-2" /> Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-slate-500">
                Visualizzando {startIndex + 1}-{Math.min(endIndex, totalItems)} di {totalItems} record
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Precedente
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  Pagina {currentPage} di {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Successiva
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ CREATE/EDIT DIALOG ============ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Modifica Ospitalità' : 'Nuova Ospitalità'}</DialogTitle>
            <DialogDescription>
              Compila i dati dell'ospitante e dell'ospitato. I dati anagrafici saranno inseriti nel PDF.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* OSPITANTE */}
            <div className="border rounded-lg p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Ospitante</Label>
                <Button size="sm" variant="ghost" onClick={() => openNewClient('ospitante')} data-testid="new-ospitante-btn">
                  <UserPlus className="w-4 h-4 mr-1" /> Nuovo cliente
                </Button>
              </div>
              <ClientPicker
                clients={clients}
                value={form.ospitante_client_id}
                onChange={(id) => setForm(f => ({ ...f, ospitante_client_id: id }))}
                placeholder="Seleziona ospitante dall'anagrafica..."
                testId="ospitante-select"
              />
              {form.ospitante_client_id && (
                <>
                  <ClientPreview client={clientMap[form.ospitante_client_id]} />
                  <ClientCompletionBanner
                    client={clientMap[form.ospitante_client_id]}
                    onSaved={(updated) => setClients(prev => prev.map(c => c.id === updated.id ? updated : c))}
                  />
                </>
              )}
            </div>

            {/* OSPITATO */}
            <div className="border rounded-lg p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Ospitato</Label>
                <Button size="sm" variant="ghost" onClick={() => openNewClient('ospitato')} data-testid="new-ospitato-btn">
                  <UserPlus className="w-4 h-4 mr-1" /> Nuovo cliente
                </Button>
              </div>
              <ClientPicker
                clients={clients}
                value={form.ospitato_client_id}
                onChange={(id) => setForm(f => ({ ...f, ospitato_client_id: id }))}
                placeholder="Seleziona ospitato dall'anagrafica..."
                testId="ospitato-select"
              />
              {form.ospitato_client_id && (
                <>
                  <ClientPreview client={clientMap[form.ospitato_client_id]} />
                  <ClientCompletionBanner
                    client={clientMap[form.ospitato_client_id]}
                    onSaved={(updated) => setClients(prev => prev.map(c => c.id === updated.id ? updated : c))}
                  />
                </>
              )}
            </div>

            {/* IMMOBILE */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Immobile dell'ospitalità</Label>
                <Button
                  size="sm" variant="outline" onClick={autofillImmobileFromOspitante}
                  disabled={!form.ospitante_client_id}
                  data-testid="autofill-immobile-btn"
                >
                  <Home className="w-4 h-4 mr-1" /> Precompila da casa
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Comune</Label>
                  <Input value={form.immobile.comune}
                         onChange={(e) => setForm(f => ({ ...f, immobile: { ...f.immobile, comune: e.target.value.toUpperCase() } }))}
                         data-testid="immobile-comune" />
                </div>
                <div>
                  <Label className="text-xs">Provincia</Label>
                  <Input value={form.immobile.provincia} maxLength={2}
                         onChange={(e) => setForm(f => ({ ...f, immobile: { ...f.immobile, provincia: e.target.value.toUpperCase() } }))} />
                </div>
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <Select value={form.immobile.tipo_via}
                          onValueChange={(v) => setForm(f => ({ ...f, immobile: { ...f.immobile, tipo_via: v } }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPI_VIA.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Denominazione</Label>
                  <Input value={form.immobile.via}
                         onChange={(e) => setForm(f => ({ ...f, immobile: { ...f.immobile, via: e.target.value.toUpperCase() } }))}
                         placeholder="es. ROMA"
                         data-testid="immobile-via" />
                </div>
                <div>
                  <Label className="text-xs">N. Civico</Label>
                  <Input value={form.immobile.civico}
                         onChange={(e) => setForm(f => ({ ...f, immobile: { ...f.immobile, civico: e.target.value.toUpperCase() } }))} />
                </div>
                <div>
                  <Label className="text-xs">Interno</Label>
                  <Input value={form.immobile.interno}
                         onChange={(e) => setForm(f => ({ ...f, immobile: { ...f.immobile, interno: e.target.value.toUpperCase() } }))} />
                </div>
                <div>
                  <Label className="text-xs">Piano</Label>
                  <Input value={form.immobile.piano}
                         onChange={(e) => setForm(f => ({ ...f, immobile: { ...f.immobile, piano: e.target.value.toUpperCase() } }))} />
                </div>
              </div>
            </div>

            {/* PERIODO */}
            <div className="border rounded-lg p-4">
              <Label className="text-base font-semibold mb-3 block">Periodo dell'ospitalità</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <Label className="text-xs">Data inizio *</Label>
                  <DateInput value={form.data_inizio}
                         onChange={(val) => setForm(f => ({ ...f, data_inizio: val }))}
                         placeholder="gg/mm/aaaa"
                         data-testid="data-inizio" />
                </div>
                <div>
                  <Label className="text-xs">Data fine</Label>
                  <DateInput value={form.data_fine}
                         disabled={form.tempo_indeterminato}
                         onChange={(val) => setForm(f => ({ ...f, data_fine: val }))}
                         placeholder="gg/mm/aaaa" />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <Checkbox
                    id="tempo_indeterminato"
                    checked={form.tempo_indeterminato}
                    onCheckedChange={(v) => setForm(f => ({ ...f, tempo_indeterminato: !!v, data_fine: v ? '' : f.data_fine }))}
                  />
                  <Label htmlFor="tempo_indeterminato" className="text-sm">Tempo indeterminato</Label>
                </div>
              </div>
            </div>

            {/* DOCUMENTO OSPITATO */}
            <div className="border rounded-lg p-4 bg-amber-50">
              <Label className="text-base font-semibold mb-1 block">Documento dell'Ospitato</Label>
              <p className="text-xs text-slate-500 mb-3">Passaporto o Permesso di Soggiorno del cittadino extracomunitario</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div>
                  <Label className="text-xs">Tipo documento</Label>
                  <Input value={form.doc_tipo}
                         placeholder="PASSAPORTO / PERMESSO"
                         onChange={(e) => setForm(f => ({ ...f, doc_tipo: e.target.value.toUpperCase() }))}
                         data-testid="doc-tipo" />
                </div>
                <div>
                  <Label className="text-xs">Nr. documento</Label>
                  <Input value={form.doc_numero}
                         onChange={(e) => setForm(f => ({ ...f, doc_numero: e.target.value.toUpperCase() }))}
                         data-testid="doc-numero" />
                </div>
                <div>
                  <Label className="text-xs">Data di rilascio</Label>
                  <DateInput value={form.doc_data_rilascio}
                         onChange={(val) => setForm(f => ({ ...f, doc_data_rilascio: val }))}
                         placeholder="gg/mm/aaaa"
                         data-testid="doc-data-rilascio" />
                </div>
                <div>
                  <Label className="text-xs">Autorità</Label>
                  <Input value={form.doc_autorita}
                         placeholder="es. QUESTURA DI PRATO"
                         onChange={(e) => setForm(f => ({ ...f, doc_autorita: e.target.value.toUpperCase() }))}
                         data-testid="doc-autorita" />
                </div>
                <div>
                  <Label className="text-xs">Cittadinanza</Label>
                  <Input value={form.cittadinanza}
                         placeholder="es. CINA"
                         onChange={(e) => setForm(f => ({ ...f, cittadinanza: e.target.value.toUpperCase() }))}
                         data-testid="cittadinanza" />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Note interne</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-ospitalita-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {form.id ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== NEW CLIENT DIALOG ========== */}
      <Dialog open={newClientDialog.open} onOpenChange={(o) => setNewClientDialog({ ...newClientDialog, open: o })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo cliente {newClientDialog.target === 'ospitante' ? 'ospitante' : 'ospitato'}</DialogTitle>
            <DialogDescription>Inserisci solo i dati essenziali. Potrai completare l'anagrafica in seguito.</DialogDescription>
          </DialogHeader>

          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-violet-800 font-medium text-sm">
              <Sparkles className="w-4 h-4" />
              Estrazione automatica dati (facoltativa)
            </div>
            <p className="text-xs text-violet-700">
              Carica il documento d'identità: i campi sotto si compilano da soli (puoi comunque correggerli a mano).
              Di solito serve anche il Permesso di Soggiorno, oltre al documento principale.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs text-violet-700">Tipo documento che stai per caricare</Label>
              <Select value={newClientDocTipo} onValueChange={setNewClientDocTipo}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NEW_CLIENT_DOC_TIPO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className={`flex items-center justify-center gap-2 border border-dashed border-violet-300 rounded-lg py-2.5 text-sm text-violet-700 hover:bg-violet-100 cursor-pointer ${extractingNewClientDoc ? 'opacity-60 pointer-events-none' : ''}`}>
              {extractingNewClientDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Carica {newClientDocTipo}
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  handleExtractNewClientDoc(f);
                  e.target.value = '';
                }}
              />
            </label>

            <label className={`flex items-center justify-center gap-2 border border-dashed border-violet-300 rounded-lg py-2.5 text-sm text-violet-700 hover:bg-violet-100 cursor-pointer ${extractingNewClientPse ? 'opacity-60 pointer-events-none' : ''}`}>
              {extractingNewClientPse ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Carica anche Permesso di Soggiorno
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  handleExtractNewClientDoc(f, 'Permesso di soggiorno', setExtractingNewClientPse, false);
                  e.target.value = '';
                }}
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Cognome *</Label>
              <Input value={newClient.last_name} onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={newClient.first_name} onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label className="text-xs">Sesso</Label>
              <Select value={newClient.gender} onValueChange={(v) => setNewClient({ ...newClient, gender: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Maschio</SelectItem>
                  <SelectItem value="F">Femmina</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Data di nascita</Label>
              <DateInput value={newClient.birth_date} onChange={(val) => setNewClient({ ...newClient, birth_date: val })} placeholder="gg/mm/aaaa" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Checkbox id="birth_abroad" checked={newClient.birth_abroad}
                        onCheckedChange={(v) => setNewClient({ ...newClient, birth_abroad: !!v })} />
              <Label htmlFor="birth_abroad" className="text-sm">Nato/a all'estero</Label>
            </div>
            {newClient.birth_abroad ? (
              <div className="col-span-2">
                <Label className="text-xs">Nazione di nascita</Label>
                <Input value={newClient.birth_country} onChange={(e) => setNewClient({ ...newClient, birth_country: e.target.value.toUpperCase() })} />
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs">Comune di nascita</Label>
                  <Input value={newClient.birth_comune} onChange={(e) => setNewClient({ ...newClient, birth_comune: e.target.value.toUpperCase() })} />
                </div>
                <div>
                  <Label className="text-xs">Provincia (sigla)</Label>
                  <Input maxLength={2} value={newClient.birth_provincia}
                         onChange={(e) => setNewClient({ ...newClient, birth_provincia: e.target.value.toUpperCase() })} />
                </div>
              </>
            )}
            <div className="col-span-2">
              <Label className="text-xs">Codice fiscale</Label>
              <div className="flex gap-2">
                <Input value={newClient.fiscal_code}
                       onChange={(e) => setNewClient({ ...newClient, fiscal_code: e.target.value.toUpperCase() })} />
                <Button type="button" variant="outline" size="sm" onClick={handleCalcolaCfDaDati}>Calcola CF</Button>
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Indirizzo residenza <span className="text-blue-600">(ricerca Google Maps)</span></Label>
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-2">
                  {GOOGLE_MAPS_API_KEY ? (
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <AddressAutocomplete
                        value={newClient.address}
                        defaultValue={newClient.address}
                        placeholder="es. Via Roma, Piazza Duomo..."
                        onPlaceSelect={(d) => {
                          const updated = {
                            ...newClient,
                            address: (d.address || newClient.address || '').toUpperCase(),
                            address_number: (d.address_number || newClient.address_number || '').toUpperCase(),
                            city: (d.city || newClient.city || '').toUpperCase(),
                            city_provincia: (d.city_provincia || newClient.city_provincia || '').toUpperCase(),
                            postal_code: d.postal_code || newClient.postal_code,
                          };
                          setNewClient(updated);
                          syncImmobileFromAddress(updated);
                        }}
                      />
                    </APIProvider>
                  ) : (
                    <Input placeholder="Via/Piazza..." value={newClient.address}
                           onChange={(e) => {
                             const updated = { ...newClient, address: e.target.value.toUpperCase() };
                             setNewClient(updated);
                             syncImmobileFromAddress(updated);
                           }} />
                  )}
                </div>
                <Input placeholder="Civico" value={newClient.address_number}
                       onChange={(e) => {
                         const updated = { ...newClient, address_number: e.target.value.toUpperCase() };
                         setNewClient(updated);
                         syncImmobileFromAddress(updated);
                       }} />
                <Input placeholder="CAP" value={newClient.postal_code}
                       onChange={(e) => setNewClient({ ...newClient, postal_code: e.target.value })} />
                <Input className="col-span-2" placeholder="Città" value={newClient.city}
                       onChange={(e) => {
                         const updated = { ...newClient, city: e.target.value.toUpperCase() };
                         setNewClient(updated);
                         syncImmobileFromAddress(updated);
                       }} />
                <Input placeholder="Prov (sigla)" maxLength={2} value={newClient.city_provincia}
                       onChange={(e) => {
                         const updated = { ...newClient, city_provincia: e.target.value.toUpperCase() };
                         setNewClient(updated);
                         syncImmobileFromAddress(updated);
                       }} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Telefono</Label>
              <Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewClientDialog({ open: false, target: null })}>Annulla</Button>
            <Button onClick={saveNewClient} disabled={creatingClient} data-testid="save-new-client-btn">
              {creatingClient && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crea e seleziona
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== PEC CONFIG DIALOG ========== */}
      <Dialog open={pecDialogOpen} onOpenChange={setPecDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configura PEC Ospitalità</DialogTitle>
            <DialogDescription>
              Server, credenziali e destinatario predefinito (es. Questura di Prato).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Email PEC *</Label>
              <Input value={pecConfig.pec_email} onChange={(e) => setPecConfig({ ...pecConfig, pec_email: e.target.value })}
                     placeholder="jvservice@pec.virgilio.it" data-testid="pec-email-input" />
            </div>
            <div>
              <Label className="text-xs">Password {pecConfigLoaded?.pec_password_set ? '(lasciare vuoto per non cambiare)' : '*'}</Label>
              <Input type="password" value={pecConfig.pec_password}
                     onChange={(e) => setPecConfig({ ...pecConfig, pec_password: e.target.value })}
                     placeholder={pecConfigLoaded?.pec_password_set ? '••••••••' : ''} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label className="text-xs">Server SMTP (invio) *</Label>
                <Input value={pecConfig.smtp_server}
                       onChange={(e) => setPecConfig({ ...pecConfig, smtp_server: e.target.value })}
                       placeholder="mail.postacert.it.net" />
              </div>
              <div>
                <Label className="text-xs">Porta *</Label>
                <Input type="number" value={pecConfig.smtp_port}
                       onChange={(e) => setPecConfig({ ...pecConfig, smtp_port: parseInt(e.target.value || 0) })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label className="text-xs">Server IMAP (ricevute)</Label>
                <Input value={pecConfig.imap_server}
                       onChange={(e) => setPecConfig({ ...pecConfig, imap_server: e.target.value })}
                       placeholder="imaps.pec.aruba.it" />
              </div>
              <div>
                <Label className="text-xs">Porta IMAP</Label>
                <Input type="number" value={pecConfig.imap_port}
                       onChange={(e) => setPecConfig({ ...pecConfig, imap_port: parseInt(e.target.value || 0) })} />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 -mt-1">
              L&apos;IMAP viene usato SOLO per scaricare le ricevute PEC (accettazione + consegna). Stessa password dell&apos;invio.
            </p>
            <div>
              <Label className="text-xs">Destinatario predefinito (Questura di Prato)</Label>
              <Input value={pecConfig.default_recipient}
                     onChange={(e) => setPecConfig({ ...pecConfig, default_recipient: e.target.value })}
                     placeholder="questuraprato@pecps.poliziadistato.it" />
            </div>

            <div className="border-t pt-3 space-y-2">
              <Label className="text-xs">Test invio a:</Label>
              <div className="flex gap-2">
                <Input value={pecTestRecipient} onChange={(e) => setPecTestRecipient(e.target.value)}
                       placeholder="tuaemail@pec.it" />
                <Button variant="outline" onClick={testPec} disabled={pecTesting}>
                  {pecTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                </Button>
              </div>
              {pecTestResult && (
                <div className={`text-sm p-2 rounded flex items-center gap-2 ${pecTestResult.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {pecTestResult.ok ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {pecTestResult.msg}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPecDialogOpen(false)}>Chiudi</Button>
            <Button onClick={savePecConfig} disabled={pecSaving} data-testid="save-pec-config-btn">
              {pecSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== SEND PEC DIALOG ========== */}
      <Dialog open={sendPecDialog.open} onOpenChange={(o) => setSendPecDialog({ ...sendPecDialog, open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia PEC</DialogTitle>
            <DialogDescription>
              Verrà inviato in allegato il PDF unico firmato caricato (dichiarazione + contratto casa + documenti).
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Destinatario PEC</Label>
            <Input value={sendPecRecipient} onChange={(e) => setSendPecRecipient(e.target.value)}
                   placeholder="questuraprato@pecps.poliziadistato.it" data-testid="send-pec-recipient" />
            <p className="text-xs text-slate-500 mt-2">
              Oggetto e corpo email vengono compilati automaticamente con i dati anagrafici.
            </p>
          </div>
          <div className="space-y-2 border-t pt-3">
            <Label className="text-xs">Invia anche una copia email di cortesia (con il PDF in allegato) a:</Label>
            <div className="flex items-center gap-2">
              <Checkbox id="copy-ospitante" checked={sendCopyToOspitante} onCheckedChange={(v) => setSendCopyToOspitante(!!v)} />
              <Label htmlFor="copy-ospitante" className="cursor-pointer font-normal">
                Ospitante {sendPecDialog.record?.ospitante_name ? `(${sendPecDialog.record.ospitante_name})` : ''}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="copy-ospitato" checked={sendCopyToOspitato} onCheckedChange={(v) => setSendCopyToOspitato(!!v)} />
              <Label htmlFor="copy-ospitato" className="cursor-pointer font-normal">
                Ospitato {sendPecDialog.record?.ospitato_name ? `(${sendPecDialog.record.ospitato_name})` : ''}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendPecDialog({ open: false, record: null })}>Annulla</Button>
            <Button onClick={confirmSendPec} disabled={sendingPec} data-testid="confirm-send-pec-btn">
              {sendingPec && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Send className="w-4 h-4 mr-2" /> Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invia ricevute PEC (accettazione/consegna) a ospitante e/o ospitato */}
      <Dialog open={ricevuteEmailDialog.open} onOpenChange={(o) => { if (!o) setRicevuteEmailDialog({ open: false, record: null, toOspitante: false, toOspitato: false }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia ricevute al cliente</DialogTitle>
            <DialogDescription>
              Invia via email il PDF con le ricevute PEC (accettazione/consegna) disponibili.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="ricevute-ospitante"
                checked={ricevuteEmailDialog.toOspitante}
                onCheckedChange={(v) => setRicevuteEmailDialog(prev => ({ ...prev, toOspitante: !!v }))}
              />
              <Label htmlFor="ricevute-ospitante" className="cursor-pointer font-normal">
                Ospitante {ricevuteEmailDialog.record?.ospitante_name ? `(${ricevuteEmailDialog.record.ospitante_name})` : ''}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="ricevute-ospitato"
                checked={ricevuteEmailDialog.toOspitato}
                onCheckedChange={(v) => setRicevuteEmailDialog(prev => ({ ...prev, toOspitato: !!v }))}
              />
              <Label htmlFor="ricevute-ospitato" className="cursor-pointer font-normal">
                Ospitato {ricevuteEmailDialog.record?.ospitato_name ? `(${ricevuteEmailDialog.record.ospitato_name})` : ''}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRicevuteEmailDialog({ open: false, record: null, toOspitante: false, toOspitato: false })}>
              Annulla
            </Button>
            <Button onClick={confirmSendRicevuteEmail} disabled={sendingRicevuteEmail === ricevuteEmailDialog.record?.id}>
              {sendingRicevuteEmail === ricevuteEmailDialog.record?.id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Mail className="w-4 h-4 mr-2" /> Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale anteprima PDF ricevuta (iframe, stessa pagina) */}
      <Dialog open={previewDialog.open} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-6xl w-[95vw] h-[92vh] p-0 flex flex-col" data-testid="preview-dialog">
          <DialogHeader className="px-4 pt-4 pb-2 border-b flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="truncate" title={previewDialog.title}>
                {previewDialog.title}
              </DialogTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const iframe = document.getElementById('ricevuta-preview-iframe');
                    if (iframe && iframe.contentWindow) {
                      try { iframe.contentWindow.print(); } catch (_e) { toast.error('Impossibile aprire il dialog di stampa'); }
                    }
                  }}
                  data-testid="preview-print-btn"
                >
                  <FileText className="w-4 h-4 mr-1" /> Stampa
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (!previewDialog.url) return;
                    const a = document.createElement('a');
                    a.href = previewDialog.url;
                    a.download = `${previewDialog.title.replace(/[^A-Za-z0-9_-]+/g, '_')}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                  }}
                  data-testid="preview-download-btn"
                >
                  <Download className="w-4 h-4 mr-1" /> Scarica
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100">
            {previewDialog.url && (
              <iframe
                id="ricevuta-preview-iframe"
                title="Anteprima ricevuta"
                src={previewDialog.url}
                className="w-full h-full border-0"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// -------- Helper Components --------
const ClientPicker = ({ clients, value, onChange, placeholder, testId }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients.slice(0, 30);
    return clients
      .filter(c => `${c.last_name} ${c.first_name} ${c.company_name || ''} ${c.fiscal_code}`.toLowerCase().includes(s))
      .slice(0, 40);
  }, [clients, q]);
  const selected = clients.find(c => c.id === value);
  return (
    <div className="relative">
      <Input
        data-testid={testId}
        placeholder={placeholder}
        value={selected ? `${selected.last_name || selected.company_name || ''} ${selected.first_name || ''}`.trim() : q}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); onChange(''); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && (
        <div className="absolute z-20 top-full left-0 right-0 bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto mt-1">
          {filtered.length === 0 ? (
            <div className="p-3 text-sm text-slate-500">Nessun cliente</div>
          ) : filtered.map(c => (
            <div
              key={c.id}
              className="p-2 hover:bg-slate-100 cursor-pointer text-sm border-b"
              onMouseDown={() => { onChange(c.id); setQ(''); setOpen(false); }}
            >
              <div className="font-medium">
                {c.is_company && c.company_name ? c.company_name : `${c.last_name || ''} ${c.first_name || ''}`}
              </div>
              <div className="text-xs text-slate-500">{c.fiscal_code} · {c.city}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ClientPreview = ({ client }) => {
  if (!client) return null;
  const bp = client.birth_abroad
    ? `${(client.birth_country || '').toUpperCase()} (EE)`
    : `${(client.birth_comune || '').toUpperCase()}${client.birth_provincia ? ` (${client.birth_provincia})` : ''}`;
  return (
    <div className="mt-2 text-xs text-slate-600 bg-white rounded p-2 border">
      CF: <span className="font-mono">{client.fiscal_code || '—'}</span>
      {' · '}Nato/a il {formatDate(client.birth_date)} a {bp || '—'}
      {' · '}Residenza: {client.address} {client.address_number}, {client.city} {client.city_provincia ? `(${client.city_provincia})` : ''}
    </div>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return iso;
    return `${d}/${m}/${y}`;
  } catch { return iso; }
};

export default Ospitalita;
